import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import Login from './Login';
import AdminPanel from './AdminPanel';
import Inventory from './Inventory';
import './App.css';

const CONDITIONS = ['Near Mint', 'Lightly Played', 'Moderately Played', 'Heavily Played', 'Damaged'];
const OWNERS = ['Ben', 'Spencer', 'Spencer\\Ben'];
const OWNER_BY_EMAIL = {
  'kingbenhamin@gmail.com': 'Ben',
  'sbates1158@gmail.com': 'Spencer',
  'dreamshardvault@gmail.com': 'Spencer\\Ben',
};

const debounce = (fn, ms) => {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), ms); };
};

const formatDate = () => {
  const d = new Date();
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const formatPrice = (val) => {
  if (!val && val !== 0) return '—';
  const n = parseFloat(val);
  return isNaN(n) ? '—' : `$${n.toFixed(2)}`;
};

// Given a Scryfall printing, determine whether the foil toggle is meaningful.
// Returns { forced: true|false|null, disabled: bool, isEtched: bool }.
// - forced=true  → this printing is foil-only (e.g. promo foil); toggle locked on.
// - forced=false → this printing is nonfoil-only; toggle locked off.
// - forced=null  → both finishes exist; toggle is user-selectable.
// - isEtched     → printing is its own etched variant; toggle is irrelevant.
const finishState = (p) => {
  if (!p) return { forced: null, disabled: false, isEtched: false };
  const finishes = Array.isArray(p.finishes) ? p.finishes : [];
  const hasNonfoil = finishes.length ? finishes.includes('nonfoil') : !!p.prices_usd;
  const hasFoil    = finishes.length ? finishes.includes('foil')    : !!p.prices_usd_foil;
  const isEtched   = !!p.prices_usd_etched && !p.prices_usd;
  if (isEtched) return { forced: false, disabled: true, isEtched: true };
  if (hasNonfoil && hasFoil) return { forced: null, disabled: false, isEtched: false };
  if (hasFoil)    return { forced: true,  disabled: true, isEtched: false };
  if (hasNonfoil) return { forced: false, disabled: true, isEtched: false };
  return { forced: null, disabled: false, isEtched: false };
};

// ─── Card Search ───────────────────────────────────────────
function CardSearch({ onSelect, resetSignal }) {
  const { authFetch } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Parent bumps resetSignal after adding to batch — clear the field and refocus
  // so the user can immediately type the next card. Skip the initial mount.
  const firstResetRef = useRef(true);
  useEffect(() => {
    if (firstResetRef.current) { firstResetRef.current = false; return; }
    setQuery('');
    setResults([]);
    setShowResults(false);
    setHighlightIdx(0);
    inputRef.current?.focus();
  }, [resetSignal]);

  const search = useCallback(
    debounce(async (q) => {
      if (q.length < 2) { setResults([]); setShowResults(false); return; }
      setLoading(true);
      try {
        const res = await authFetch(`/api/autocomplete?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        setResults(data.data || []);
        setHighlightIdx(0);
        setShowResults(true);
      } catch (e) { console.error(e); }
      setLoading(false);
    }, 250),
    [authFetch]
  );

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectCard = async (name) => {
    setQuery(name);
    setShowResults(false);
    setLoading(true);
    try {
      const [cardRes, printsRes] = await Promise.all([
        authFetch(`/api/card?name=${encodeURIComponent(name)}`),
        authFetch(`/api/printings?name=${encodeURIComponent(name)}`)
      ]);
      const card = await cardRes.json();
      const prints = await printsRes.json();
      if (!card.error) {
        onSelect(card, prints.printings || [card], !!prints.has_more);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (showResults && results.length > 0) selectCard(results[highlightIdx]);
      else if (query.length >= 2) selectCard(query);
    }
    else if (e.key === 'Escape') setShowResults(false);
  };

  return (
    <div ref={wrapperRef} className="search-wrap">
      <input
        ref={inputRef}
        type="text" value={query}
        onChange={(e) => { setQuery(e.target.value); search(e.target.value); }}
        onKeyDown={handleKeyDown}
        onFocus={() => results.length > 0 && setShowResults(true)}
        placeholder="Search card name..."
        className="search-input"
        autoFocus
      />
      {loading && <div className="search-spinner">⟳</div>}
      {showResults && results.length > 0 && (
        <div className="dropdown">
          {results.map((name, idx) => (
            <div key={name}
              className={`dropdown-item ${idx === highlightIdx ? 'active' : ''}`}
              onClick={() => selectCard(name)}
              onMouseEnter={() => setHighlightIdx(idx)}>
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Printing Selector ─────────────────────────────────────
function PrintingSelector({ printings, selected, onSelect, onLoadMore, loadingMore }) {
  const [filter, setFilter] = useState('');
  if (!printings || printings.length <= 1) return null;
  const q = filter.trim().toLowerCase();
  const filtered = q
    ? printings.filter(p =>
        (p.set_name || '').toLowerCase().includes(q) ||
        (p.set_code || '').toLowerCase().includes(q)
      )
    : printings;
  return (
    <div className="printings-wrap">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
        <label className="label" style={{ marginBottom: 0 }}>
          Set / Printing ({filtered.length}{q && filtered.length !== printings.length ? ` / ${printings.length}` : ''})
        </label>
        <input
          type="text"
          placeholder="Filter by set or code…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ flex: '0 1 220px', padding: '4px 8px', fontSize: 12 }}
        />
      </div>
      <div className="printings-grid">
        {filtered.map((p, i) => (
          <div key={p.id || i}
            className={`printing-card ${selected?.id === p.id ? 'selected' : ''}`}
            onClick={() => onSelect(p)}>
            {p.image_small
              ? <img src={p.image_small} alt={p.name} className="printing-img" />
              : <div className="printing-img no-img">No img</div>
            }
            <div className="printing-set">{p.set_name}</div>
            <div className="printing-num">#{p.collector_number}</div>
            <div className="printing-price">
              {p.prices_usd
                ? `$${p.prices_usd}`
                : p.prices_usd_etched
                  ? `$${p.prices_usd_etched} etched`
                  : p.prices_usd_foil
                    ? `$${p.prices_usd_foil} foil`
                    : '—'}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ color: '#666', fontSize: 12, padding: '12px 4px' }}>No printings match "{filter}".</div>
        )}
      </div>
      {onLoadMore && (
        <button className="btn-cancel" style={{ marginTop: 10 }} onClick={onLoadMore} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : 'Load more printings'}
        </button>
      )}
    </div>
  );
}

// ─── ManaBox Import Modal ──────────────────────────────────
function ImportManaBox({ onClose, onImported }) {
  const { authFetch } = useAuth();
  const [file, setFile] = useState(null);
  const [minValue, setMinValue] = useState('1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // When pending_special_foil is non-empty, we hold the API response and show
  // a review step before pushing to the batch.
  const [review, setReview] = useState(null);
  const [choices, setChoices] = useState({}); // idx -> 'accept' | 'reject'

  const runImport = async () => {
    if (!file) { setError('Pick a CSV file first'); return; }
    setBusy(true);
    setError('');
    try {
      const csv = await file.text();
      const res = await authFetch('/api/manabox-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv, minValue: parseFloat(minValue) || 0 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Import failed');
      const pending = data.pending_special_foil || [];
      if (pending.length === 0) {
        onImported(data);
      } else {
        setReview(data);
        setChoices({});
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const allChosen = review
    ? (review.pending_special_foil || []).every((_, i) => choices[i] === 'accept' || choices[i] === 'reject')
    : false;

  const finishReview = () => {
    if (!review) return;
    const pending = review.pending_special_foil || [];
    const merged = pending.map((p, i) => {
      // Strip the review-only fields before pushing to the batch.
      const { originalTcgplayerId, candidateTcgplayerId, candidateTcgplayerSkuId, candidateName, ...item } = p;
      if (choices[i] === 'accept') {
        return { ...item, tcgplayerId: candidateTcgplayerId, tcgplayerSkuId: candidateTcgplayerSkuId };
      }
      // Rejected: keep as regular foil with the original TCG product (no SKU,
      // same fallback as a non-detected case).
      return { ...item, tcgplayerSkuId: '' };
    });
    const finalData = {
      ...review,
      items:    [...(review.items || []), ...merged],
      imported: (review.items || []).length + merged.length,
    };
    onImported(finalData);
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-panel" onClick={(e) => e.stopPropagation()}>
        <div className="admin-header">
          <div className="admin-title">
            {review ? 'Confirm special-foil matches' : 'Import from ManaBox'}
          </div>
          <button className="admin-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="admin-error" style={{ padding: '10px 20px', color: '#f77' }}>{error}</div>}
        {!review && (
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label className="label">ManaBox CSV file</label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={busy}
              />
            </div>
            <div>
              <label className="label">Minimum USD value (cards below this are skipped)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={minValue}
                onChange={(e) => setMinValue(e.target.value)}
                disabled={busy}
                style={{ width: 120 }}
              />
            </div>
            <div className="btn-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn-add" onClick={runImport} disabled={busy || !file}>
                {busy ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        )}
        {review && (
          <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: '#bbb' }}>
              These cards are flagged as foil in your CSV, but Scryfall doesn't list a foil SKU
              for the linked TCGPlayer product. We found a sibling product whose name matches —
              confirm whether it's the actual variant you have.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '50vh', overflowY: 'auto' }}>
              {(review.pending_special_foil || []).map((p, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 12, padding: 10,
                  border: '1px solid #333', borderRadius: 6, alignItems: 'center'
                }}>
                  {p.image && (
                    <img src={p.image} alt={p.name}
                      style={{ width: 50, height: 70, objectFit: 'cover', borderRadius: 3 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>
                      {p.set} · #{p.number} · qty {p.quantity}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      Match: <span style={{ color: '#9cf' }}>{p.candidateName}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => setChoices(c => ({ ...c, [i]: 'accept' }))}
                      style={{
                        padding: '6px 10px', borderRadius: 4, cursor: 'pointer',
                        background: choices[i] === 'accept' ? '#2a6' : '#333',
                        color: '#fff', border: 'none',
                      }}
                    >Yes</button>
                    <button
                      onClick={() => setChoices(c => ({ ...c, [i]: 'reject' }))}
                      style={{
                        padding: '6px 10px', borderRadius: 4, cursor: 'pointer',
                        background: choices[i] === 'reject' ? '#a44' : '#333',
                        color: '#fff', border: 'none',
                      }}
                    >No</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="btn-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-cancel" onClick={onClose}>Cancel</button>
              <button className="btn-add" onClick={finishReview} disabled={!allChosen}>
                Add to batch
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TCG Marketplace Price Updater Modal ───────────────────
function UpdatePrices({ onClose, userEmail }) {
  const { authFetch } = useAuth();
  const [file, setFile] = useState(null);
  const ownerLabel = userEmail ? OWNER_BY_EMAIL[userEmail.toLowerCase()] : null;
  const [updateOwn, setUpdateOwn] = useState(!!ownerLabel);
  const [updateJoint, setUpdateJoint] = useState(false);
  const [shipRate, setShipRate] = useState('1.49');
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [progress, setProgress] = useState(null);  // { done, total } during live pricing
  const [preview, setPreview] = useState(null);     // result awaiting continue/cancel
  const [summary, setSummary] = useState(null);     // result after the user downloads
  const [error, setError] = useState('');

  const downloadFile = (content, filename, mime) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // The backend prices every owned SKU live (a minute or two for big inventories),
  // streaming NDJSON progress. We read those events to drive a progress bar, then
  // stage a preview — nothing downloads until the user reviews the diff and confirms.
  const run = async () => {
    if (!file) { setError('Pick the TCGPlayer CSV first'); return; }
    if (!updateOwn && !updateJoint) { setError('Turn on at least one toggle'); return; }
    setBusy(true); setError(''); setSummary(null); setPreview(null);
    setStatusMsg('Starting…'); setProgress(null);
    try {
      const csv = await file.text();
      const res = await authFetch('/api/prices/update-csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv, updateOwn, updateJoint, shipRate: parseFloat(shipRate) || 0 }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Update failed (${res.status})`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let result = null;
      const handleLine = (raw) => {
        const line = raw.trim();
        if (!line) return;
        let ev;
        try { ev = JSON.parse(line); } catch { return; }
        if (ev.type === 'status') setStatusMsg(ev.message);
        else if (ev.type === 'progress') setProgress({ done: ev.done, total: ev.total });
        else if (ev.type === 'error') throw new Error(ev.error);
        else if (ev.type === 'result') result = ev;
      };
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf('\n')) >= 0) {
          handleLine(buf.slice(0, nl));
          buf = buf.slice(nl + 1);
        }
      }
      if (buf.trim()) handleLine(buf);
      if (!result) throw new Error('The stream ended before pricing finished — please try again.');
      setPreview(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Continue: write the files the preview produced, then show the final summary.
  const confirmDownload = () => {
    if (!preview) return;
    const ts = Date.now();
    downloadFile(preview.csv, `tcgplayer-prices-${ts}.csv`, 'text/csv');
    if (preview.dropped && preview.dropped.length) {
      const header = 'Name\tSet\tNumber\tCondition\tSKU';
      const lines = preview.dropped.map(d =>
        `${d.name}\t${d.set}\t${d.number}\t${d.condition}\t${d.sku}`
      );
      downloadFile(
        [header, ...lines].join('\r\n'),
        `untracked-cards-${ts}.tsv`,
        'text/tab-separated-values',
      );
    }
    setSummary(preview);
    setPreview(null);
  };

  const cancelPreview = () => { setPreview(null); setProgress(null); setStatusMsg(''); };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-panel" onClick={(e) => e.stopPropagation()}>
        <div className="admin-header">
          <div className="admin-title">Update TCG Marketplace Prices</div>
          <button className="admin-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="admin-error" style={{ padding: '10px 20px', color: '#f77' }}>{error}</div>}
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className="label">TCGPlayer pricing CSV export</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setSummary(null); }}
              disabled={busy}
            />
            <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
              Each owned row is priced live against TCGPlayer's English listings —
              the same Match as Batch Refresh (lowest listing total minus your
              shipping below). This takes a minute or two; you'll see progress and a
              current→new preview to confirm before anything downloads. Rows for
              cards not tracked in the sheet (under the selected owners) are dropped
              and listed in a separate download.
            </div>
          </div>
          <div>
            <label className="label">Your shipping rate ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={shipRate}
              onChange={(e) => setShipRate(e.target.value)}
              disabled={busy}
              style={{ width: 100 }}
            />
            <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
              Subtracted from each card's lowest total so your price + shipping ties theirs.
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={updateOwn}
              onChange={(e) => setUpdateOwn(e.target.checked)}
              disabled={busy || !ownerLabel}
            />
            Update my prices {ownerLabel ? `(${ownerLabel})` : '— no owner mapped to your email'}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={updateJoint}
              onChange={(e) => setUpdateJoint(e.target.checked)}
              disabled={busy}
            />
            {'Update joint-owned prices (Spencer\\Ben)'}
          </label>
          {busy && (
            <div style={{ background: '#1a1a1a', padding: 12, borderRadius: 6, fontSize: 13 }}>
              <div style={{ color: '#ccc' }}>{statusMsg || 'Working…'}</div>
              {progress && progress.total > 0 ? (
                <>
                  <div style={{ background: '#222', borderRadius: 4, height: 8, overflow: 'hidden', marginTop: 8 }}>
                    <div style={{
                      width: `${Math.round((100 * progress.done) / progress.total)}%`,
                      height: '100%', background: '#4a90d9', transition: 'width .2s',
                    }} />
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                    Priced {progress.done} / {progress.total} cards live against TCGPlayer…
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                  This can take a minute or two for large inventories.
                </div>
              )}
            </div>
          )}

          {preview && (() => {
            const fmt = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
            const rows = preview.rows || [];
            const priced = rows.filter((r) => r.old != null && r.new != null);
            const sumOld = priced.reduce((s, r) => s + r.old, 0);
            const sumNew = priced.reduce((s, r) => s + r.new, 0);
            const ups = priced.filter((r) => r.new > r.old + 0.005).length;
            const downs = priced.filter((r) => r.new < r.old - 0.005).length;
            const net = sumNew - sumOld;
            const sorted = [...rows].sort((a, b) => {
              const da = (a.old != null && a.new != null) ? Math.abs(a.new - a.old) : -1;
              const db = (b.old != null && b.new != null) ? Math.abs(b.new - b.old) : -1;
              return db - da;
            });
            return (
              <div style={{ background: '#1a1a1a', padding: 12, borderRadius: 6, fontSize: 13 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>Review {preview.updated}</strong> price change{preview.updated === 1 ? '' : 's'} before downloading.{' '}
                  <span style={{ color: '#7ec699' }}>↑{ups}</span>{' '}
                  <span style={{ color: '#e08a8a' }}>↓{downs}</span>
                  {' · '}current {fmt(sumOld)} → new <strong>{fmt(sumNew)}</strong>{' '}
                  (<span style={{ color: net >= 0 ? '#7ec699' : '#e08a8a' }}>{net >= 0 ? '+' : ''}{fmt(net)}</span>)
                </div>
                <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #2a2a2a', borderRadius: 4 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, background: '#222', color: '#aaa', textAlign: 'left' }}>
                        <th style={{ padding: '6px 8px' }}>Card</th>
                        <th style={{ padding: '6px 8px' }}>Cond</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Current</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>New</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((r, i) => {
                        const hasDelta = r.old != null && r.new != null;
                        const delta = hasDelta ? r.new - r.old : null;
                        const color = !hasDelta ? '#888'
                          : delta > 0.005 ? '#7ec699'
                          : delta < -0.005 ? '#e08a8a' : '#aaa';
                        return (
                          <tr key={i} style={{ borderTop: '1px solid #242424' }}>
                            <td style={{ padding: '5px 8px' }}>{r.name}{r.set ? <span style={{ color: '#666' }}> · {r.set}</span> : null}</td>
                            <td style={{ padding: '5px 8px', color: '#999' }}>{r.condition}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', color: '#999' }}>{fmt(r.old)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.new == null ? '—' : fmt(r.new)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', color }}>
                              {!hasDelta ? (r.new == null ? 'skip' : 'new') : `${delta >= 0 ? '+' : ''}${delta.toFixed(2)}`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {preview.clamped_min > 0 && (
                  <div style={{ color: '#fbb', marginTop: 8, fontSize: 12 }}>⚠ {preview.clamped_min} couldn't be matched (a rival's total is below your shipping) — listed at the ${preview.min_price?.toFixed?.(2) ?? preview.min_price} minimum.</div>
                )}
                {(preview.used_floor_fallback > 0 || preview.used_scryfall_fallback > 0) && (
                  <div style={{ color: '#caa', marginTop: 4, fontSize: 12 }}>
                    {preview.used_floor_fallback > 0 && <span>{preview.used_floor_fallback} via TCG floor. </span>}
                    {preview.used_scryfall_fallback > 0 && <span>{preview.used_scryfall_fallback} via Scryfall. </span>}
                    Live listings were unavailable for these — Cancel and re-run to retry.
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                  Cancel to change shipping/owners and re-run.
                </div>
              </div>
            );
          })()}

          {summary && (
            <div style={{ background: '#1a1a1a', padding: 12, borderRadius: 6, fontSize: 13, lineHeight: 1.6 }}>
              <div style={{ color: '#aaa', marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #2a2a2a' }}>
                Sheet: <strong>{summary.sheet_rows_eligible}</strong> owned row{summary.sheet_rows_eligible === 1 ? '' : 's'} →{' '}
                <strong>{summary.sheet_unique_skus}</strong> unique SKU{summary.sheet_unique_skus === 1 ? '' : 's'}
                {summary.unresolved_sheet > 0 && (
                  <span> ({summary.unresolved_sheet} unresolved)</span>
                )}
                <br/>
                TCG CSV: <strong>{summary.csv_rows}</strong> row{summary.csv_rows === 1 ? '' : 's'} →{' '}
                <strong>{summary.matched}</strong> matched
                {summary.other_owner > 0 && (
                  <span>, <strong>{summary.other_owner}</strong> other-owner</span>
                )}
                , <strong>{summary.dropped_count}</strong> untracked
              </div>
              <div><strong>{summary.updated}</strong> price{summary.updated === 1 ? '' : 's'} updated.</div>
              {summary.matched_with_shipping > 0 && (
                <div><strong>{summary.matched_with_shipping}</strong> matched to the live English lowest total − ${summary.ship_rate?.toFixed?.(2) ?? summary.ship_rate} shipping.</div>
              )}
              {summary.clamped_min > 0 && (
                <div style={{ color: '#fbb' }}>⚠ <strong>{summary.clamped_min}</strong> couldn't be matched — a rival's total is below your ${summary.ship_rate?.toFixed?.(2) ?? summary.ship_rate} shipping, so they're listed at the ${summary.min_price?.toFixed?.(2) ?? summary.min_price} minimum.</div>
              )}
              {summary.used_floor_fallback > 0 && (
                <div><strong>{summary.used_floor_fallback}</strong> used the TCGPlayer floor (live listings unavailable — not shipping-normalized).</div>
              )}
              {summary.used_scryfall_fallback > 0 && (
                <div><strong>{summary.used_scryfall_fallback}</strong> used Scryfall market (no live TCG price).</div>
              )}
              {summary.skipped_no_price > 0 && (
                <div><strong>{summary.skipped_no_price}</strong> matched rows skipped (no live, floor, or market price).</div>
              )}
              <div><strong>{summary.dropped_count}</strong> untracked row{summary.dropped_count === 1 ? '' : 's'} dropped{summary.dropped_count > 0 ? ' — see downloaded list' : ''}.</div>
              {summary.unresolved_sheet > 0 && (
                <div style={{ color: '#fbb' }}>
                  ⚠ {summary.unresolved_sheet} owned card(s) couldn't be resolved to a SKU and may have been missed.
                </div>
              )}
            </div>
          )}
          <div className="btn-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn-cancel" onClick={onClose} disabled={busy}>Close</button>
            {preview ? (
              <>
                <button className="btn-cancel" onClick={cancelPreview} disabled={busy}>Cancel</button>
                <button className="btn-add" onClick={confirmDownload} disabled={busy}>
                  Continue &amp; Download ({preview.updated})
                </button>
              </>
            ) : (
              <button className="btn-add" onClick={run} disabled={busy || !file}>
                {busy ? 'Pricing…' : 'Update Prices'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Match Transactions Modal ──────────────────────────────
// Upload a TCGPlayer seller-portal PDF; backend parses + matches each line
// item to an unsold Data-sheet row; user reviews the diff and approves.
function MatchTransactions({ onClose, userEmail }) {
  const { authFetch } = useAuth();
  const userOwner = userEmail ? OWNER_BY_EMAIL[userEmail.toLowerCase()] : null;
  const [file, setFile] = useState(null);
  // Re-match (audit) mode: instead of recording a new sale, re-check an
  // already-recorded order and overwrite its Net Prof / Sold Date. Drives the
  // parse request; the preview/apply read parsed.audit (the server echo).
  const [audit, setAudit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [parsed, setParsed] = useState(null);  // server response
  // For each duplicate line item: chosen sheet_row (the candidate the user picked).
  // Keyed by line-item index.
  const [picks, setPicks] = useState({});
  const [applied, setApplied] = useState(null);
  // Floating card preview that follows the cursor while hovering a line item or
  // a grab-list row. Self-contained inside the modal so it stays above the
  // overlay z-index without depending on the App-level preview.
  const [hover, setHover] = useState(null);  // { src, x, y }
  const hoverEnter = (src) => (e) => src && setHover({ src, x: e.clientX, y: e.clientY });
  const hoverMove  = (e) => setHover(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
  const hoverLeave = () => setHover(null);

  const fileToBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      // Strip the data: prefix, keep just the base64 body.
      const comma = dataUrl.indexOf(',');
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    reader.onerror = () => reject(new Error('Failed to read PDF'));
    reader.readAsDataURL(f);
  });

  const runParse = async () => {
    if (!file) { setError('Pick the seller-portal PDF first'); return; }
    setBusy(true);
    setError('');
    setParsed(null);
    setApplied(null);
    try {
      const b64 = await fileToBase64(file);
      const res = await authFetch('/api/tcg-parse-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pdf_base64: b64, audit }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Parse failed');
      setParsed(data);
      // Default duplicate picks to none — user must explicitly choose.
      setPicks({});
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const blockingItems = parsed
    ? parsed.line_items.filter(li => li.status === 'unmatched' || li.status === 'unparseable')
    : [];
  const dupIndices = parsed
    ? parsed.line_items.map((li, i) => li.status === 'duplicate' ? i : -1).filter(i => i >= 0)
    : [];
  const allDupsChosen = dupIndices.every(i => picks[i]);
  // How many rows would actually be written (matched copies + chosen duplicates).
  const resolvedRowCount = parsed
    ? parsed.line_items.reduce((n, li, idx) => (
        li.status === 'matched' ? n + li.candidates.length
        : (li.status === 'duplicate' && picks[idx]) ? n + 1
        : n), 0)
    : 0;
  // Audit mode never blocks on unmatched line items — the user inspects those by
  // hand; we just write whatever did match. Normal mode still requires zero
  // blocking items before approving a brand-new sale.
  const canApprove = parsed && allDupsChosen && resolvedRowCount > 0 && !applied
    && (parsed.audit || blockingItems.length === 0);

  const runApply = async () => {
    if (!parsed || !canApprove) return;
    setBusy(true);
    setError('');
    try {
      // Build the rows payload — one row per (matched or chosen-from-duplicate) candidate.
      const rows = [];
      parsed.line_items.forEach((li, idx) => {
        if (li.status === 'matched') {
          // For matched items, take all candidates (quantity == candidates.length).
          li.candidates.forEach(c => rows.push({
            sheet_row: c.sheet_row,
            net_profit: li.per_card_net,
          }));
        } else if (li.status === 'duplicate') {
          const picked = picks[idx];
          if (!picked) return;
          // For now duplicate only handles qty=1 (the rule was: pick whose to use).
          // Quantity > 1 with duplicates is rare enough that we fall back to picking once.
          rows.push({ sheet_row: picked, net_profit: li.per_card_net });
        }
      });
      if (!rows.length) throw new Error('Nothing to write');
      const res = await authFetch('/api/tcg-apply-matches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id:  parsed.order_id,
          sold_date: parsed.sold_date,
          overwrite: parsed.audit,
          rows,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.conflicts) {
          throw new Error(`${data.error} (rows: ${data.conflicts.map(c => c.sheet_row).join(', ')})`);
        }
        throw new Error(data.error || 'Write failed');
      }
      setApplied(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const moneyStr = (v) => (typeof v === 'number' ? `$${v.toFixed(2)}` : '—');
  const netStr   = (v) => (typeof v === 'number' ? `$${v.toFixed(4)}` : '—');

  // Audit-mode helpers: compare an existing sheet value with the recomputed one.
  const numOrNull = (v) => {
    if (v === '' || v == null) return null;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isNaN(n) ? null : n;
  };
  const netEq = (a, b) => {
    const fa = numOrNull(a), fb = numOrNull(b);
    if (fa == null || fb == null) return String(a ?? '').trim() === String(b ?? '').trim();
    return Math.abs(fa - fb) < 0.005;  // within half a cent → treat as unchanged
  };
  // Render "proposed" green when unchanged, or "old → new" (old struck-through)
  // when the recompute differs from what's currently on the sheet.
  const diffSpan = (cur, prop, same) => same
    ? <span style={{ color: '#9c9' }}>{prop}</span>
    : <span>
        <span style={{ color: '#e88', textDecoration: 'line-through' }}>{cur}</span>
        {' → '}
        <span style={{ color: '#9f9', fontWeight: 600 }}>{prop}</span>
      </span>;

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900 }}>
        <div className="admin-header">
          <div className="admin-title">
            {applied
              ? (parsed?.audit ? 'Order updated' : 'Order marked sold')
              : parsed
                ? `Order ${parsed.order_id}${parsed.audit ? ' · re-match' : ''}`
                : 'Match Transactions'}
          </div>
          <button className="admin-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="admin-error" style={{ padding: '10px 20px', color: '#f77' }}>{error}</div>}

        {/* Step 1 — file upload */}
        {!parsed && (
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label className="label">TCGPlayer seller-portal PDF (Print &rarr; Save as PDF)</label>
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={busy}
              />
              <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                Open the order in the seller portal, print to PDF, and upload it here. The tool will
                parse the order&apos;s Net Amount, match each line item to your Data sheet, and show
                you what would change before writing.
              </div>
            </div>
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13,
              cursor: 'pointer', padding: 10, borderRadius: 6,
              background: audit ? '#2a2333' : '#1a1a1a',
              border: `1px solid ${audit ? '#6a5a8a' : '#333'}`,
            }}>
              <input
                type="checkbox"
                checked={audit}
                onChange={(e) => setAudit(e.target.checked)}
                disabled={busy}
                style={{ marginTop: 2 }}
              />
              <span style={{ color: '#ccc' }}>
                <strong style={{ color: audit ? '#caf' : '#ddd' }}>Re-match an already-recorded order</strong>{' '}
                — fix the Net Prof / Sold Date on a past order instead of recording a new sale. Only rows
                already tagged with this order&apos;s number are considered; you&apos;ll see current vs.
                recomputed values and approve the overwrite.
              </span>
            </label>
            <div className="btn-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn-add" onClick={runParse} disabled={busy || !file}>
                {busy ? 'Parsing…' : 'Parse PDF'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — preview & approve */}
        {parsed && !applied && (
          <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: '#1a1a1a', padding: 12, borderRadius: 6, fontSize: 13, lineHeight: 1.6 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <span>Product: <strong>{moneyStr(parsed.product_amount)}</strong></span>
                <span>Shipping: <strong>{moneyStr(parsed.shipping_amount)}</strong></span>
                <span>Order: <strong>{moneyStr(parsed.order_amount)}</strong></span>
                <span>Fee: <strong>({moneyStr(parsed.fee_amount)})</strong></span>
                <span style={{ color: '#9f9' }}>Net: <strong>{moneyStr(parsed.net_amount)}</strong></span>
                <span>Factor: <strong>{parsed.apportion_factor}</strong></span>
                <span>Sold: <strong>{parsed.sold_date}</strong></span>
              </div>
            </div>

            {/* Shipping responsibility — whoever's cards make up ≥70% of the
                order's product value pays shipping; otherwise it's a split
                order. Joint (Spencer\Ben) cards split 50/50, matching the
                Total-Owed convention. Computed over resolved candidates
                (matched rows + the picked copy of each duplicate). Not shown when
                re-matching an old order — those cards already shipped. */}
            {!parsed.audit && (() => {
              const SHIP_THRESHOLD = 0.70;
              const classify = (owner) => {
                const o = (owner || '').toLowerCase();
                const hasBen = o.includes('ben');
                const hasSpencer = o.includes('spencer');
                if (hasBen && hasSpencer) return 'joint';
                if (hasBen) return 'ben';
                if (hasSpencer) return 'spencer';
                return null;
              };
              let ben = 0, spencer = 0;
              const add = (owner, val) => {
                const k = classify(owner);
                if (k === 'joint') { ben += val / 2; spencer += val / 2; }
                else if (k === 'ben') ben += val;
                else if (k === 'spencer') spencer += val;
              };
              parsed.line_items.forEach((li, idx) => {
                const perCard = (li.ext_price || 0) / (li.quantity || 1);
                if (li.status === 'matched') {
                  li.candidates.forEach(c => add(c.owner, perCard));
                } else if (li.status === 'duplicate') {
                  const picked = li.candidates.find(c => c.sheet_row === picks[idx]);
                  if (picked) add(picked.owner, perCard);
                }
              });
              const total = ben + spencer;
              if (total <= 0) return null;
              const benPct = ben / total;
              const spencerPct = spencer / total;
              const hasUnresolved = blockingItems.length > 0 || dupIndices.some(i => !picks[i]);

              let label, payerColor;
              if (benPct >= SHIP_THRESHOLD) { label = 'Ben pays shipping'; payerColor = '#9cf'; }
              else if (spencerPct >= SHIP_THRESHOLD) { label = 'Spencer pays shipping'; payerColor = '#9cf'; }
              else { label = 'Split order'; payerColor = '#fc9'; }

              return (
                <div style={{
                  background: '#13202b', border: `1px solid ${payerColor}44`, borderRadius: 6,
                  padding: '10px 12px', display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                  gap: 14, fontSize: 13,
                }}>
                  <span style={{ fontWeight: 600, color: payerColor }}>📮 {label}</span>
                  <span style={{ color: '#aaa' }}>
                    Ben <strong style={{ color: '#ddd' }}>{(benPct * 100).toFixed(0)}%</strong>
                    {' · '}
                    Spencer <strong style={{ color: '#ddd' }}>{(spencerPct * 100).toFixed(0)}%</strong>
                  </span>
                  {hasUnresolved && (
                    <span style={{ color: '#888', fontSize: 11 }}>
                      (resolved cards only — pick duplicates / add missing rows for a final split)
                    </span>
                  )}
                </div>
              );
            })()}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '55vh', overflowY: 'auto' }}>
              {parsed.line_items.map((li, i) => {
                const statusColors = {
                  matched:     { bg: '#163', border: '#2a6' },
                  duplicate:   { bg: '#553', border: '#aa6' },
                  unmatched:   { bg: '#633', border: '#a44' },
                  unparseable: { bg: '#633', border: '#a44' },
                };
                const sc = statusColors[li.status] || { bg: '#222', border: '#444' };
                // First candidate's image is representative of the card itself
                // (all candidates of a duplicate set are the same printing).
                const hoverImg = (li.candidates && li.candidates[0]
                                  && (li.candidates[0].image_normal || li.candidates[0].image_small)) || '';
                return (
                  <div
                    key={i}
                    style={{
                      border: `1px solid ${sc.border}`, borderRadius: 6, padding: 10,
                      background: '#1a1a1a',
                    }}
                    onMouseEnter={hoverImg ? hoverEnter(hoverImg) : undefined}
                    onMouseMove={hoverImg ? hoverMove : undefined}
                    onMouseLeave={hoverImg ? hoverLeave : undefined}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>
                          {li.card_name || <em style={{ color: '#f77' }}>Could not parse: {li.raw}</em>}
                        </div>
                        {li.card_name && (
                          <div style={{ fontSize: 12, color: '#999' }}>
                            {li.set_name} · #{li.number} · {li.condition_abbrev}{li.foil ? ' · FOIL' : ''}
                            {li.quantity > 1 ? ` · qty ${li.quantity}` : ''}
                            {' · '}ext {moneyStr(li.ext_price)} → net {netStr(li.per_card_net)} ea.
                          </div>
                        )}
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10,
                        background: sc.bg, color: '#fff', whiteSpace: 'nowrap',
                      }}>
                        {li.status === 'matched' ? '✓ matched'
                         : li.status === 'duplicate' ? (li.match_note ? '⚠ confirm' : '⚠ pick one')
                         : li.status === 'unmatched' ? '✗ not in data'
                         : '✗ unparseable'}
                      </span>
                    </div>

                    {li.match_note && (
                      <div style={{ fontSize: 12, marginTop: 6, color: li.status === 'duplicate' ? '#fc9' : '#9cf' }}>
                        ℹ {li.match_note}
                      </div>
                    )}

                    {/* Matched: list the row(s) that will be written. In audit
                        mode show current → recomputed Net Prof / Sold Date so the
                        user can see exactly what the overwrite changes. */}
                    {li.status === 'matched' && (
                      <div style={{ fontSize: 12, marginTop: 8, color: '#cfc' }}>
                        {li.candidates.map((c, j) => (
                          <div key={j} style={{ marginBottom: parsed.audit ? 6 : 0 }}>
                            row {c.sheet_row}: <span style={{ color: '#9cf' }}>{c.owner || '(no owner)'}</span>
                            {' '}· {c.set} #{c.number} {c.condition}{c.foil ? '/'+c.foil : ''}
                            {parsed.audit ? (
                              <div style={{ marginTop: 2, color: '#bbb' }}>
                                Net Prof: {diffSpan(netStr(numOrNull(c.cur_net_prof)), netStr(li.per_card_net),
                                                    netEq(c.cur_net_prof, li.per_card_net))}
                                {'   ·   '}
                                Sold: {diffSpan(c.cur_sold || '—', parsed.sold_date,
                                                (c.cur_sold || '').trim() === (parsed.sold_date || '').trim())}
                              </div>
                            ) : (
                              <>
                                {' → '}
                                <span style={{ color: '#fff' }}>
                                  Order={parsed.order_id.slice(0, 8)}…, Net={netStr(li.per_card_net)}, Sold={parsed.sold_date}
                                </span>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Duplicate: ask the user which row to attribute it to. */}
                    {li.status === 'duplicate' && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 12, color: '#ddc', marginBottom: 6 }}>
                          {li.match_note
                            ? `Check the card, then pick the row ${parsed.audit ? 'to overwrite' : 'to mark sold'}:`
                            : `${li.candidates.length} copies match. Pick whose ${parsed.audit ? 'to overwrite' : 'to mark sold'}:`}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {li.candidates.map((c) => (
                            <label key={c.sheet_row} style={{
                              display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                              padding: '4px 6px', borderRadius: 4,
                              background: picks[i] === c.sheet_row ? '#2a3a2a' : 'transparent',
                              cursor: 'pointer',
                            }}>
                              <input
                                type="radio"
                                name={`dup-${i}`}
                                checked={picks[i] === c.sheet_row}
                                onChange={() => setPicks(p => ({ ...p, [i]: c.sheet_row }))}
                              />
                              {parsed.audit ? (
                                <span><strong>{c.owner || '(no owner)'}</strong> — row {c.sheet_row}, Net {netStr(numOrNull(c.cur_net_prof))}, Sold {c.cur_sold || '—'}</span>
                              ) : (
                                <span><strong>{c.owner || '(no owner)'}</strong> — row {c.sheet_row}, {c.set} #{c.number} {c.condition}{c.foil ? '/'+c.foil : ''}, listed {c.listed || '?'}, ${c.cur_value || '?'}</span>
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Unmatched: spell out the reason so the user can fix the sheet. */}
                    {(li.status === 'unmatched' || li.status === 'unparseable') && (
                      <div style={{ fontSize: 12, marginTop: 8, color: '#fbb' }}>
                        {parsed.audit && li.status === 'unmatched'
                          ? 'No row tagged to this order matches this card — skipped, review by hand.'
                          : (li.reason || 'Could not parse this product line')}
                        {li.candidates && li.candidates.length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            Existing rows (already sold or other condition/foil):
                            {li.candidates.map(c => (
                              <div key={c.sheet_row} style={{ color: '#aaa' }}>
                                · row {c.sheet_row}: {c.owner} · {c.condition}{c.foil ? '/'+c.foil : ''}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* "Cards to grab" — filtered to the logged-in user's physical inventory.
                For Ben/Spencer: their own rows + joint. For the shared dreamshardvault
                account (joint): all rows in the order. Skipped when re-matching an old
                order — those cards already shipped. */}
            {!parsed.audit && (() => {
              if (!userOwner) return null;
              const ownerMatches = (rowOwner) => {
                if (!rowOwner) return false;
                if (userOwner === 'Spencer\\Ben') return true;  // shared account sees all
                const o = rowOwner.toLowerCase();
                const u = userOwner.toLowerCase();
                return o === u || o.includes(u + '\\') || o.includes('\\' + u);
              };
              const grabRows = [];
              parsed.line_items.forEach((li, idx) => {
                if (!li.candidates || !li.candidates.length) return;
                if (li.status === 'matched') {
                  li.candidates.forEach(c => {
                    if (ownerMatches(c.owner)) {
                      grabRows.push({ li, c, idx });
                    }
                  });
                } else if (li.status === 'duplicate') {
                  const pickedRow = picks[idx];
                  if (!pickedRow) return;
                  const picked = li.candidates.find(c => c.sheet_row === pickedRow);
                  if (picked && ownerMatches(picked.owner)) {
                    grabRows.push({ li, c: picked, idx });
                  }
                }
              });
              if (!grabRows.length) return null;
              return (
                <div style={{
                  background: '#13202b', border: '1px solid #2a4a6a', borderRadius: 6,
                  padding: 14, display: 'flex', flexDirection: 'column', gap: 12,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#9cf' }}>
                    📦 Cards to grab — {userOwner} ({grabRows.length})
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 10,
                  }}>
                    {grabRows.map(({ li, c, idx }) => {
                      const hImg = c.image_normal || c.image_small;
                      return (
                        <div
                          key={`${idx}-${c.sheet_row}`}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12, fontSize: 13,
                            padding: 8, borderRadius: 4, background: '#0e1822',
                            border: '1px solid #1f3245',
                          }}
                          onMouseEnter={hImg ? hoverEnter(hImg) : undefined}
                          onMouseMove={hImg ? hoverMove : undefined}
                          onMouseLeave={hImg ? hoverLeave : undefined}
                        >
                          {c.image_small ? (
                            <img src={c.image_small} alt={li.card_name}
                              style={{ width: 73, height: 102, objectFit: 'cover',
                                       borderRadius: 4, border: '1px solid #333', flexShrink: 0 }} />
                          ) : <div style={{ width: 73, height: 102, flexShrink: 0 }} />}
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {li.card_name}
                            </div>
                            <div style={{ fontSize: 11, color: '#aaa', marginTop: 4, lineHeight: 1.5 }}>
                              <div>{c.set} #{c.number} · {c.condition}{c.foil ? '/'+c.foil : ''}</div>
                              <div><span style={{ color: '#9cf' }}>{c.owner}</span> · row {c.sheet_row}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Audit only: rows tagged to this order that no PDF line claimed —
                a mis-tag, or a line missing from this print. Never auto-touched. */}
            {parsed.audit && parsed.orphan_rows && parsed.orphan_rows.length > 0 && (
              <div style={{ background: '#2a2333', border: '1px solid #6a5a8a', borderRadius: 6, padding: 12, fontSize: 12 }}>
                <div style={{ fontWeight: 600, color: '#caf', marginBottom: 6 }}>
                  ⚠ {parsed.orphan_rows.length} row{parsed.orphan_rows.length === 1 ? '' : 's'} tagged to this order but not on the PDF
                </div>
                <div style={{ color: '#aaa', marginBottom: 8 }}>
                  These won&apos;t be changed — review by hand (a mis-tagged card, or a line missing from this print).
                </div>
                {parsed.orphan_rows.map(o => (
                  <div key={o.sheet_row} style={{ color: '#ccc' }}>
                    · row {o.sheet_row}: {o.name} · {o.set} #{o.number} {o.condition}{o.foil ? '/'+o.foil : ''}
                    {' · '}<span style={{ color: '#9cf' }}>{o.owner}</span>
                    {' · '}Net {netStr(numOrNull(o.cur_net_prof))}, Sold {o.cur_sold || '—'}
                  </div>
                ))}
              </div>
            )}

            {blockingItems.length > 0 && (
              <div style={{ fontSize: 12, color: '#fbb', padding: 8, background: '#311', borderRadius: 4 }}>
                {parsed.audit ? (
                  <>{blockingItems.length} line item{blockingItems.length === 1 ? '' : 's'} on this PDF
                  {blockingItems.length === 1 ? " isn't" : " aren't"} tagged to this order — skipped. Approve to
                  write the rest, and review {blockingItems.length === 1 ? 'it' : 'them'} by hand.</>
                ) : (
                  <>{blockingItems.length} line item{blockingItems.length === 1 ? '' : 's'} can&apos;t be matched.
                  Add the missing row{blockingItems.length === 1 ? '' : 's'} to your Data sheet and re-upload.</>
                )}
              </div>
            )}

            <div className="btn-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
              <button
                className="btn-add"
                onClick={runApply}
                disabled={busy || !canApprove}
                title={!canApprove
                  ? (parsed.audit ? 'Pick a copy for each duplicate first' : 'Resolve duplicates and unmatched rows first')
                  : (parsed.audit ? 'Overwrite Net Prof / Sold Date on the tagged rows' : 'Write changes to Data sheet')}
              >
                {busy ? 'Writing…' : (parsed.audit ? 'Approve & Overwrite' : 'Approve & Write to Sheet')}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — success */}
        {applied && (
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ color: '#9f9', fontSize: 14 }}>
              {parsed?.audit ? 'Updated' : 'Wrote'} <strong>{applied.updated}</strong> row{applied.updated === 1 ? '' : 's'} {parsed?.audit ? 'on' : 'to'} the Data sheet.
            </div>
            <div className="btn-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-add" onClick={onClose}>Done</button>
            </div>
          </div>
        )}
      </div>

      {/* Floating card preview that follows the cursor — rendered at the modal
          root so it sits above all panel content. Same viewport-flip logic as
          the main App hover preview. */}
      {hover && (
        <img
          src={hover.src}
          alt=""
          style={{
            position: 'fixed',
            left: hover.x + 260 > window.innerWidth ? hover.x - 260 : hover.x + 20,
            top:  hover.y + 360 > window.innerHeight ? window.innerHeight - 360 : hover.y + 10,
            width: 240,
            pointerEvents: 'none',
            zIndex: 2000,
            borderRadius: 10,
            boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
          }}
        />
      )}
    </div>
  );
}


// ─── Match Payments Modal ──────────────────────────────────
// Upload a TCGPlayer "Payment Order List" PDF (one payout batch). Backend
// reconciles each order's Total Sale - Total Fees against the sum of its cards'
// Net Prof and flags problems; user records the payout date on each card and
// enters refund amounts for partial refunds.
const PAY_STATUS = {
  ok:                 { label: '✓ ready',         bg: '#163', border: '#2a6' },
  mismatch:           { label: '✗ totals off',    bg: '#633', border: '#a44' },
  not_matched:        { label: '✗ not matched',   bg: '#633', border: '#a44' },
  refunded_unmatched: { label: '↩ refunded (skip)', bg: '#333', border: '#555' },
  already_paid:       { label: '• already paid',  bg: '#2a2a33', border: '#555' },
  late_refund:        { label: '↩ late refund',   bg: '#643', border: '#c83' },
};

// Every Mon/Thu payday since the first recorded Pay Date — recorded, no payout
// (nothing was owed, or marked by hand for store closures), or missing — so
// it's obvious which Payment Order List PDFs still need uploading.
const PAYDAY_STYLE = {
  recorded:  { icon: '✓', border: '#2a6' },
  no_payout: { icon: '–', border: '#555' },
  missing:   { icon: '✗', border: '#a44' },
};

function PaymentTracker() {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [missingOnly, setMissingOnly] = useState(false);
  const [busyDate, setBusyDate] = useState(null);

  const load = useCallback(() => {
    return authFetch('/api/payment-tracker')
      .then(async res => {
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || 'Failed to load tracker');
        setData(d);
      })
      .catch(e => setError(e.message));
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const setNoPayout = async (b, mark) => {
    let note = '';
    if (mark) {
      note = window.prompt(`Mark ${b.weekday} ${b.date} as no payout? Optional note:`, 'store closed');
      if (note === null) return;
    }
    setBusyDate(b.date_iso); setError('');
    try {
      const res = await authFetch('/api/payment-tracker/no-payout', {
        method: mark ? 'POST' : 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: b.date_iso, note }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Update failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyDate(null);
    }
  };

  if (!data) {
    return error
      ? <div style={{ fontSize: 12, color: '#f77' }}>{error}</div>
      : <div style={{ fontSize: 12, color: '#888' }}>Loading payment history…</div>;
  }
  if (!data.batches.length) return <div style={{ fontSize: 12, color: '#888' }}>No payments recorded yet.</div>;

  const filtered = missingOnly ? data.batches.filter(b => b.status === 'missing') : data.batches;
  const shown = showAll ? filtered : filtered.slice(0, 16);
  const smallBtn = { fontSize: 11, padding: '1px 6px', marginLeft: 6 };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
        <label className="label" style={{ margin: 0 }}>Payment batches (Mon / Thu)</label>
        <span style={{ fontSize: 12, color: '#999' }}>
          <span style={{ color: '#6c6' }}>{data.recorded_count} recorded</span>
          {' · '}{data.no_payout_count} no payout{' · '}
          <span style={{ color: data.missing_count ? '#f99' : '#999' }}>{data.missing_count} missing</span>
          <label style={{ marginLeft: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={missingOnly} onChange={e => setMissingOnly(e.target.checked)} /> missing only
          </label>
        </span>
      </div>
      {error && <div style={{ fontSize: 12, color: '#f77', marginBottom: 6 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
        {shown.map(b => {
          const st = PAYDAY_STYLE[b.status] || PAYDAY_STYLE.missing;
          const busy = busyDate === b.date_iso;
          const title = b.off_schedule ? 'Recorded on a day that isn’t a Mon/Thu payday'
            : b.paid_on ? `Holiday shift — paid ${b.paid_on_weekday} ${b.paid_on}`
            : b.no_payout_reason === 'nothing_owed' ? 'Every card sold before this date was already paid — nothing was owed'
            : b.no_payout_reason === 'marked' ? `Marked no payout${b.note ? ` — ${b.note}` : ''} (NoPayouts tab)`
            : undefined;
          return (
            <div key={b.date_iso} title={title} style={{
              fontSize: 12, padding: '5px 8px', borderRadius: 4, background: '#1a1a1a',
              border: `1px solid ${b.off_schedule ? '#a83' : st.border}`,
              color: b.status === 'no_payout' ? '#888' : undefined,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
            }}>
              <span>
                {st.icon} {b.weekday} {b.date}
                {b.paid_on && <span style={{ color: '#9cf' }}> → {b.paid_on_weekday}</span>}
              </span>
              <span style={{ color: '#999', whiteSpace: 'nowrap' }}>
                {b.status === 'recorded' && `${b.cards} · $${b.net.toFixed(2)}`}
                {b.status === 'no_payout' && (b.no_payout_reason === 'marked' ? (b.note || 'no payout') : 'nothing owed')}
                {b.status === 'no_payout' && b.no_payout_reason === 'marked' && (
                  <button className="btn-cancel" style={smallBtn} disabled={busy}
                    onClick={() => setNoPayout(b, false)} title="Unmark — count this payday as missing again">
                    {busy ? '…' : 'undo'}
                  </button>
                )}
                {b.status === 'missing' && (
                  <button className="btn-cancel" style={smallBtn} disabled={busy}
                    onClick={() => setNoPayout(b, true)} title="Store closed / no payout this day">
                    {busy ? '…' : 'no payout'}
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {filtered.length > 16 && (
        <button className="btn-cancel" style={{ marginTop: 6, fontSize: 12, padding: '3px 10px' }}
          onClick={() => setShowAll(s => !s)}>
          {showAll ? 'Show recent only' : `Show all ${filtered.length}`}
        </button>
      )}
    </div>
  );
}

function MatchPayments({ onClose }) {
  const { authFetch } = useAuth();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [parsed, setParsed] = useState(null);   // server response
  const [payDate, setPayDate] = useState('');    // editable payout date (Pay Date col)
  // Partial-refund inputs, keyed by sheet_row.
  const [amounts, setAmounts] = useState({});    // sheet_row -> string
  const [reasons, setReasons] = useState({});    // sheet_row -> string
  const [applied, setApplied] = useState(null);
  const [adjAck, setAdjAck] = useState(false);   // adjustments reviewed by hand

  const fileToBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const comma = dataUrl.indexOf(',');
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    reader.onerror = () => reject(new Error('Failed to read PDF'));
    reader.readAsDataURL(f);
  });

  const runParse = async () => {
    if (!file) { setError('Pick the Payment Order List PDF first'); return; }
    setBusy(true); setError(''); setParsed(null); setApplied(null);
    try {
      const b64 = await fileToBase64(file);
      const res = await authFetch('/api/payment-parse-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pdf_base64: b64 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Parse failed');
      setParsed(data);
      setPayDate(data.payment_date || '');
      setAmounts({}); setReasons({}); setAdjAck(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const money = (v) => (typeof v === 'number' ? `$${v.toFixed(2)}` : '—');

  // Build the rows to write: every non-already-paid card under an 'ok' order.
  // Full refund → amount auto-set to the card's Net Prof; partial → user-typed
  // amount (skipped if blank/zero); none → paid only.
  const lateReason = (sr) => reasons[sr] || `Late refund — deducted from ${payDate.trim()} payment`;
  const buildRows = () => {
    if (!parsed) return [];
    const rows = [];
    parsed.orders.forEach(o => {
      // Late refund: cards were paid in an earlier batch — write only the refund.
      if (o.status === 'late_refund') {
        o.cards.forEach(c => {
          const amt = o.refund_kind === 'full' ? c.net_prof : parseFloat(amounts[c.sheet_row]);
          if (typeof amt === 'number' && !isNaN(amt) && amt > 0) {
            rows.push({ sheet_row: c.sheet_row, refund_amount: amt,
                        refund_reason: lateReason(c.sheet_row), late_refund: true });
          }
        });
        return;
      }
      if (o.status !== 'ok') return;
      o.cards.forEach(c => {
        if (c.already_paid) return;
        const row = { sheet_row: c.sheet_row };
        if (o.refund_kind === 'full') {
          if (typeof c.net_prof === 'number') row.refund_amount = c.net_prof;
          if (reasons[c.sheet_row]) row.refund_reason = reasons[c.sheet_row];
        } else if (o.refund_kind === 'partial') {
          const amt = parseFloat(amounts[c.sheet_row]);
          if (!isNaN(amt) && amt > 0) {
            row.refund_amount = amt;
            if (reasons[c.sheet_row]) row.refund_reason = reasons[c.sheet_row];
          }
        }
        rows.push(row);
      });
    });
    return rows;
  };

  const writableRows = parsed ? buildRows() : [];
  const lateRows = writableRows.filter(r => r.late_refund);
  const paidRows = writableRows.filter(r => !r.late_refund);
  // A partial late refund blocks until at least one card's amount is entered,
  // so a clawback can't slip through unrecorded.
  const blockingOrders = parsed
    ? parsed.orders.filter(o => o.status === 'mismatch' || o.status === 'not_matched'
        || (o.status === 'late_refund' && !lateRows.some(r => o.cards.some(c => c.sheet_row === r.sheet_row))))
    : [];
  const hasAdjustments = !!(parsed && parsed.adjustments && parsed.adjustments.length);
  const canConfirm = parsed && !applied && blockingOrders.length === 0
    && writableRows.length > 0 && !!payDate.trim() && (!hasAdjustments || adjAck);

  const runApply = async () => {
    if (!canConfirm) return;
    setBusy(true); setError('');
    try {
      const res = await authFetch('/api/payment-apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_date: payDate.trim(), rows: writableRows }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.conflicts) {
          throw new Error(`${data.error} (rows: ${data.conflicts.map(c => c.sheet_row).join(', ')})`);
        }
        throw new Error(data.error || 'Write failed');
      }
      setApplied(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const shortId = (id) => (id && id.length > 14 ? id.slice(0, 8) + '…' + id.slice(-5) : id);

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 940 }}>
        <div className="admin-header">
          <div className="admin-title">
            {applied ? 'Payment recorded' : parsed ? `Payment · ${parsed.payment_date}` : 'Match Payments'}
          </div>
          <button className="admin-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="admin-error" style={{ padding: '10px 20px', color: '#f77' }}>{error}</div>}

        {/* Step 1 — file upload */}
        {!parsed && (
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label className="label">TCGPlayer Payment Order List PDF (Print &rarr; Save as PDF)</label>
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={busy}
              />
              <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                Open the payout under Payments in the seller portal, print to PDF, and upload it here.
                Each order is reconciled against your Data sheet&apos;s Net Prof; you&apos;ll see any
                orders that aren&apos;t matched yet or don&apos;t add up before anything is written.
              </div>
            </div>
            <div className="btn-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn-add" onClick={runParse} disabled={busy || !file}>
                {busy ? 'Parsing…' : 'Parse PDF'}
              </button>
            </div>
            <PaymentTracker />
          </div>
        )}

        {/* Step 2 — preview & confirm */}
        {parsed && !applied && (
          <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: '#1a1a1a', padding: 12, borderRadius: 6, fontSize: 13,
                          display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#999' }}>Pay Date (written to every paid card):</span>
                <input type="text" value={payDate} onChange={e => setPayDate(e.target.value)}
                  style={{ width: 110, padding: '4px 6px' }} />
              </label>
              <span style={{ color: '#999' }}>{parsed.orders.length} orders</span>
              <span style={{ color: '#9cf' }}>{paidRows.length} cards to mark paid</span>
              {lateRows.length > 0 && <span style={{ color: '#fc9' }}>{lateRows.length} late refund{lateRows.length === 1 ? '' : 's'}</span>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '58vh', overflowY: 'auto' }}>
              {parsed.orders.map((o, i) => {
                const sc = PAY_STATUS[o.status] || { label: o.status, bg: '#222', border: '#444' };
                const reconcileBad = o.status === 'mismatch';
                return (
                  <div key={i} style={{ border: `1px solid ${sc.border}`, borderRadius: 6, padding: 10, background: '#1a1a1a' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>
                          {o.buyer || '(no buyer)'}{' '}
                          <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>{shortId(o.order_id)} · {o.order_date}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                          Sale {money(o.total_sale)} − Fees {money(o.total_fees)} = <strong style={{ color: '#ccc' }}>{money(o.order_net)}</strong>
                          {' · '}cards’ Net Prof <strong style={{ color: reconcileBad ? '#f99' : '#ccc' }}>{money(o.sum_net_prof)}</strong>
                          {reconcileBad && <span style={{ color: '#f99' }}> · off by {money(o.reconcile_diff)}</span>}
                          {o.refund_kind !== 'none' && <span style={{ color: '#fc9' }}> · {o.refund_kind} refund {money(o.refunded_orders)}</span>}
                        </div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10,
                                     background: sc.bg, color: '#fff', whiteSpace: 'nowrap' }}>
                        {sc.label}
                      </span>
                    </div>

                    {/* Blocking: not matched */}
                    {o.status === 'not_matched' && (
                      <div style={{ fontSize: 12, marginTop: 8, color: '#fbb' }}>
                        No cards in the Data sheet carry this Order #. Complete this transaction
                        (Match Transactions) first, then re-upload the payment.
                      </div>
                    )}

                    {/* Blocking: totals mismatch */}
                    {o.status === 'mismatch' && (
                      <div style={{ fontSize: 12, marginTop: 8, color: '#fbb' }}>
                        Payout net ({money(o.order_net)}) doesn&apos;t match the sum of this order&apos;s
                        cards ({money(o.sum_net_prof)}). Fix the Net Prof / order data, then re-upload.
                        {o.cards.length > 0 && (
                          <div style={{ marginTop: 4, color: '#caa' }}>
                            {o.cards.map(c => (
                              <div key={c.sheet_row}>· row {c.sheet_row}: {c.name} — {c.owner} · Net {money(c.net_prof)}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Info: fully refunded & never matched — nothing to do */}
                    {o.status === 'refunded_unmatched' && (
                      <div style={{ fontSize: 12, marginTop: 8, color: '#aaa' }}>
                        Fully refunded and not in the sheet — treated as a reversed sale and skipped.
                      </div>
                    )}

                    {/* Info: already paid */}
                    {o.status === 'already_paid' && (
                      <div style={{ fontSize: 12, marginTop: 8, color: '#aaa' }}>
                        Every card already has a Pay Date
                        {o.cards[0]?.existing_pay_date ? ` (${o.cards[0].existing_pay_date})` : ''} — skipped.
                      </div>
                    )}

                    {/* Late refund: paid in an earlier batch, clawed back in this one */}
                    {o.status === 'late_refund' && (
                      <div style={{ fontSize: 12, marginTop: 8, color: '#fc9' }}>
                        Already paid{o.cards[0]?.existing_pay_date ? ` on ${o.cards[0].existing_pay_date}` : ''}, and
                        this payment deducts a {o.refund_kind} refund of {money(o.refund_net)} net. Only Amount
                        Refunded + Reason are written — the original Pay Date stays.
                        {o.refund_kind === 'partial' && ' Enter how much was refunded per card (must total at least one card).'}
                      </div>
                    )}

                    {/* Ready: list cards and refund inputs */}
                    {(o.status === 'ok' || o.status === 'late_refund') && (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {o.status === 'ok' && o.refund_kind === 'partial' && (
                          <div style={{ fontSize: 12, color: '#fc9' }}>
                            Partial refund — PDF shows {money(o.refund_net)} net refunded. Enter how much was
                            refunded per card (leave blank for cards that weren&apos;t refunded).
                          </div>
                        )}
                        {o.status === 'ok' && o.refund_kind === 'full' && (
                          <div style={{ fontSize: 12, color: '#fc9' }}>
                            Full refund — each card&apos;s Amount Refunded is set to its Net Prof (nets to $0).
                          </div>
                        )}
                        {o.cards.map(c => { const editable = !c.already_paid || o.status === 'late_refund'; return (
                          <div key={c.sheet_row} style={{
                            display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
                            padding: '4px 6px', borderRadius: 4,
                            background: editable ? '#0e1822' : '#222',
                            border: '1px solid #1f3245', opacity: editable ? 1 : 0.6,
                          }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontWeight: 600 }}>{c.name}</span>{' '}
                              <span style={{ color: '#9cf' }}>{c.owner || '(no owner)'}</span>
                              <span style={{ color: '#888' }}> · {c.set} #{c.number} {c.condition}{c.foil ? '/'+c.foil : ''} · row {c.sheet_row} · Net {money(c.net_prof)}</span>
                              {c.already_paid && <span style={{ color: '#888' }}> · paid {c.existing_pay_date || ''}</span>}
                            </div>
                            {editable && o.refund_kind === 'partial' && (
                              <>
                                <input type="number" step="0.01" min="0" placeholder="refund $"
                                  value={amounts[c.sheet_row] || ''}
                                  onChange={e => setAmounts(a => ({ ...a, [c.sheet_row]: e.target.value }))}
                                  style={{ width: 80, padding: '3px 5px' }} />
                                <input type="text" placeholder={o.status === 'late_refund' ? 'late refund' : 'reason'}
                                  value={reasons[c.sheet_row] || ''}
                                  onChange={e => setReasons(r => ({ ...r, [c.sheet_row]: e.target.value }))}
                                  style={{ width: 140, padding: '3px 5px' }} />
                              </>
                            )}
                            {editable && o.refund_kind === 'full' && (
                              <>
                                <span style={{ color: '#fc9', width: 80, textAlign: 'right' }}>↩ {money(c.net_prof)}</span>
                                <input type="text" placeholder={o.status === 'late_refund' ? 'late refund' : 'reason'}
                                  value={reasons[c.sheet_row] || ''}
                                  onChange={e => setReasons(r => ({ ...r, [c.sheet_row]: e.target.value }))}
                                  style={{ width: 140, padding: '3px 5px' }} />
                              </>
                            )}
                          </div>
                        ); })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {parsed.adjustments && parsed.adjustments.length > 0 && (
              <div style={{ fontSize: 12, color: '#fc9', padding: 8, background: '#2a2410', borderRadius: 4 }}>
                Adjustments in this batch (not tied to cards — review manually). A refund on an
                order paid in an earlier batch can land here; record it on the card by hand.
                {parsed.adjustments.map((a, j) => <div key={j} style={{ color: '#ddd' }}>· {a}</div>)}
                <label style={{ display: 'block', marginTop: 6, cursor: 'pointer', color: '#fc9' }}>
                  <input type="checkbox" checked={adjAck} onChange={e => setAdjAck(e.target.checked)} />{' '}
                  I&apos;ve handled these adjustments
                </label>
              </div>
            )}

            {blockingOrders.length > 0 && (
              <div style={{ fontSize: 12, color: '#fbb', padding: 8, background: '#311', borderRadius: 4 }}>
                {blockingOrders.length} order{blockingOrders.length === 1 ? '' : 's'} need
                {blockingOrders.length === 1 ? 's' : ''} attention before you can record this payment.
              </div>
            )}

            <div className="btn-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
              <button
                className="btn-add"
                onClick={runApply}
                disabled={busy || !canConfirm}
                title={blockingOrders.length > 0 ? 'Resolve the flagged orders first'
                       : writableRows.length === 0 ? 'Nothing new to record'
                       : hasAdjustments && !adjAck ? 'Tick “I’ve handled these adjustments” first'
                       : 'Write Pay Date + refunds to the Data sheet'}
              >
                {busy ? 'Writing…' : `Record Payment (${writableRows.length})`}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — success */}
        {applied && (
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ color: '#9f9', fontSize: 14 }}>
              Marked <strong>{applied.updated}</strong> card{applied.updated === 1 ? '' : 's'} paid for {payDate}.
            </div>
            <div className="btn-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-add" onClick={onClose}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Edit Batch Item Modal ─────────────────────────────────
function EditBatchItem({ item, onClose, onSave }) {
  const { authFetch } = useAuth();
  const [condition, setCondition] = useState(item.condition);
  const [foil, setFoil] = useState(item.foil);
  const [owner, setOwner] = useState(item.owner);
  const [quantity, setQuantity] = useState(item.quantity);
  const [curValue, setCurValue] = useState(item.curValue);
  const [printings, setPrintings] = useState([]);
  const [printingsPage, setPrintingsPage] = useState(1);
  const [printingsHasMore, setPrintingsHasMore] = useState(false);
  const [loadingMorePrintings, setLoadingMorePrintings] = useState(false);
  const [selectedPrinting, setSelectedPrinting] = useState(null);
  const [busy, setBusy] = useState(false);

  // Pull every printing of this card so the user can swap version from the edit modal.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`/api/printings?name=${encodeURIComponent(item.name)}`);
        const data = await res.json();
        if (cancelled) return;
        const all = data.printings || [];
        setPrintings(all);
        setPrintingsPage(1);
        setPrintingsHasMore(!!data.has_more);
        // Match the current row by (set_code, collector_number) — unique per printing on Scryfall.
        const match = all.find(p => p.collector_number === item.number && p.set_code === item.setCode);
        setSelectedPrinting(match || all[0] || null);
      } catch (e) { /* modal still works without the carousel */ }
    })();
    return () => { cancelled = true; };
  }, [item.name, item.number, item.setCode, authFetch]);

  const loadMorePrintings = async () => {
    if (!printingsHasMore || loadingMorePrintings) return;
    setLoadingMorePrintings(true);
    try {
      const next = printingsPage + 1;
      const res = await authFetch(`/api/printings?name=${encodeURIComponent(item.name)}&page=${next}`);
      const data = await res.json();
      setPrintings(prev => [...prev, ...(data.printings || [])]);
      setPrintingsPage(next);
      setPrintingsHasMore(!!data.has_more);
    } catch (e) { /* noop */ }
    setLoadingMorePrintings(false);
  };

  const finishInfo = finishState(selectedPrinting);
  useEffect(() => {
    if (finishInfo.forced !== null && foil !== finishInfo.forced) {
      setFoil(finishInfo.forced);
    }
  }, [finishInfo.forced, foil]);

  const save = async () => {
    setBusy(true);
    const updated = { ...item, condition, tcgCondition: condition, foil, owner, quantity, curValue };

    const printingChanged = selectedPrinting && (
      selectedPrinting.collector_number !== item.number ||
      selectedPrinting.set_code !== item.setCode
    );
    const isEtched = !!(selectedPrinting?.prices_usd_etched && !selectedPrinting?.prices_usd);
    if (printingChanged) {
      updated.set         = selectedPrinting.set_name;
      updated.setCode     = selectedPrinting.set_code;
      updated.number      = selectedPrinting.collector_number;
      updated.tcgplayerId = (isEtched
        ? (selectedPrinting.tcgplayer_etched_id || selectedPrinting.tcgplayer_id)
        : selectedPrinting.tcgplayer_id) || '';
      updated.image       = selectedPrinting.image_normal || '';
      updated.url         = selectedPrinting.scryfall_url || '';
    }

    // SKU is condition+finish+product specific — re-fetch if any of the three changed.
    const skuNeedsRefresh = (
      condition !== item.condition ||
      foil !== item.foil ||
      printingChanged
    ) && updated.tcgplayerId;
    if (skuNeedsRefresh) {
      try {
        const finishParam = isEtched ? '&finish=etched' : '';
        const nameParam = `&name=${encodeURIComponent(updated.name || '')}`;
        const res = await authFetch(
          `/api/sku?pid=${updated.tcgplayerId}&condition=${encodeURIComponent(condition)}&foil=${foil}${finishParam}${nameParam}`
        );
        const data = await res.json();
        updated.tcgplayerSkuId = data.sku_id || '';
        // Same special-foil sibling swap as addToBatch — when foil lookup hits
        // a name-matched sibling product, take that product's ID too.
        if (data.special_foil_match?.tcg_pid) {
          updated.tcgplayerId = data.special_foil_match.tcg_pid;
        }
      } catch (e) {
        // Fall through with the old SKU — CSV still exports, user can re-edit.
      }
    }

    // Re-fetch price when the printing or foil changed, unless the user manually
    // overrode the price field (respect their typed value).
    const priceUntouched = String(curValue) === String(item.curValue);
    const priceNeedsRefresh = (printingChanged || foil !== item.foil) && priceUntouched && updated.setCode && updated.number;
    if (priceNeedsRefresh) {
      try {
        const res = await authFetch('/api/prices/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify([updated]),
        });
        const data = await res.json();
        const r = data?.prices?.[0] || {};
        if (r.price !== null && r.price !== undefined) {
          updated.curValue = String(r.price);
        }
        if (r.tcgLow !== null && r.tcgLow !== undefined) {
          updated.tcgLow = String(r.tcgLow);
          updated.priceSource = r.source || 'scryfall';
        }
      } catch (e) {
        // Keep the old price on failure — user can hit the batch-wide refresh.
      }
    }

    onSave(updated);
    setBusy(false);
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-panel" onClick={(e) => e.stopPropagation()}>
        <div className="admin-header">
          <div className="admin-title">Edit · {item.name}</div>
          <button className="admin-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="condition-foil-row">
            <div className="condition-part">
              <label className="label">Condition</label>
              <div className="radio-group">
                {CONDITIONS.map(c => (
                  <label key={c} className={`radio-option${condition === c ? ' selected' : ''}`}>
                    <input type="radio" name="edit-condition" value={c} checked={condition === c} onChange={() => setCondition(c)} />
                    {c.replace('Near Mint','NM').replace('Lightly Played','LP').replace('Moderately Played','MP').replace('Heavily Played','HP').replace('Damaged','DMG')}
                  </label>
                ))}
              </div>
            </div>
            <div className="foil-field">
              <label className="label">{finishInfo.isEtched ? 'Etched' : 'Foil'}</label>
              <div
                className={`toggle ${foil ? 'on' : ''} ${finishInfo.disabled ? 'disabled' : ''}`}
                onClick={() => { if (!finishInfo.disabled) setFoil(!foil); }}
                title={
                  finishInfo.isEtched ? 'Etched printing — toggle not applicable' :
                  finishInfo.forced === true  ? 'This printing is foil-only' :
                  finishInfo.forced === false ? 'This printing is non-foil only' : ''
                }>
                <div className="toggle-dot" />
              </div>
            </div>
          </div>
          <div>
            <label className="label">Owner</label>
            <select value={owner} onChange={e => setOwner(e.target.value)}>
              {OWNERS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Quantity</label>
            <input type="number" min={1} value={quantity} onChange={e => setQuantity(parseInt(e.target.value) || 1)} />
          </div>
          <div>
            <label className="label">Price</label>
            <input type="text" value={curValue} onChange={e => setCurValue(e.target.value)} />
          </div>
          <PrintingSelector
            printings={printings}
            selected={selectedPrinting}
            onSelect={setSelectedPrinting}
            onLoadMore={printingsHasMore ? loadMorePrintings : null}
            loadingMore={loadingMorePrintings}
          />
          <div className="btn-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn-add" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main App ──────────────────────────────────────────────
function App() {
  const { user, loading, logout, authFetch } = useAuth();
  const [card, setCard] = useState(null);
  const [printings, setPrintings] = useState([]);
  const [printingsPage, setPrintingsPage] = useState(1);
  const [printingsHasMore, setPrintingsHasMore] = useState(false);
  const [loadingMorePrintings, setLoadingMorePrintings] = useState(false);
  const [selectedPrinting, setSelectedPrinting] = useState(null);
  const [condition, setCondition] = useState('Near Mint');
  const [foil, setFoil] = useState(false);
  const [owner, setOwner] = useState(OWNERS[0]);

  // Default the Owner field based on the signed-in user
  useEffect(() => {
    const mapped = user?.email && OWNER_BY_EMAIL[user.email.toLowerCase()];
    if (mapped) setOwner(mapped);
  }, [user?.email]);
  const [customPrice, setCustomPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [batch, setBatch] = useState([]);
  const [stagingHydrated, setStagingHydrated] = useState(false);
  const [toast, setToast] = useState({ msg: '', show: false });
  const [showAdmin, setShowAdmin] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showPriceUpdate, setShowPriceUpdate] = useState(false);
  const [showMatchTx, setShowMatchTx] = useState(false);
  const [showMatchPay, setShowMatchPay] = useState(false);
  const [showInventory, setShowInventory] = useState(false);
  const [hoverPreview, setHoverPreview] = useState(null);  // { image, x, y, idx } — drives the row-hover card preview
  const [editingIdx, setEditingIdx] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [toolsOpen, setToolsOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [shipRate, setShipRate] = useState('1.49');  // our flat shipping, for TCG-low matching
  const [searchResetSignal, setSearchResetSignal] = useState(0);
  // Pre-push SKU check: when non-null, holds { pushable, missing } and the
  // confirmation modal is shown before the actual push runs.
  const [pushWarning, setPushWarning] = useState(null);
  const toolsRef = useRef(null);

  // Close the Tools dropdown when clicking anywhere outside it.
  useEffect(() => {
    if (!toolsOpen) return;
    const handler = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setToolsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [toolsOpen]);

  // The row's onMouseLeave is the only thing that clears hoverPreview, so it
  // gets stranded whenever the hovered row unmounts (remove, clear, finalize,
  // filter change) or when a modal covers the table without the cursor moving.
  // Clear it whenever the referenced row is no longer there, or a modal opens.
  useEffect(() => {
    if (!hoverPreview) return;
    const row = batch[hoverPreview.idx];
    if (!row || row.image !== hoverPreview.image || editingIdx !== null || showAdmin || showImport || showPriceUpdate || showMatchTx || showMatchPay || showInventory) {
      setHoverPreview(null);
    }
  }, [batch, hoverPreview, editingIdx, showAdmin, showImport, showPriceUpdate, showMatchTx, showMatchPay, showInventory]);
  // Tracks the last batch payload we know the server has. Used by the poll to
  // decide whether a fetched server state is actually new (vs. just echoing
  // back our own save), which also prevents the poll from clobbering rapid
  // local edits that haven't flushed through the debounce yet.
  const lastSyncedRef = useRef(null);

  // Load the user's saved batch from the server once we know who they are.
  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;
    setStagingHydrated(false);
    (async () => {
      try {
        const res = await authFetch('/api/staging');
        if (!cancelled && res.ok) {
          const data = await res.json();
          const items = data.items || [];
          setBatch(items);
          lastSyncedRef.current = JSON.stringify(items);
        }
      } finally {
        if (!cancelled) setStagingHydrated(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.email, authFetch]);

  // Persist batch changes back to the server (debounced).
  useEffect(() => {
    if (!stagingHydrated) return;
    const payload = JSON.stringify(batch);
    const t = setTimeout(async () => {
      try {
        const res = await authFetch('/api/staging', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        });
        if (res.ok) lastSyncedRef.current = payload;
      } catch (e) { /* noop — next change will retry */ }
    }, 800);
    return () => clearTimeout(t);
  }, [batch, stagingHydrated, authFetch]);

  // Poll for changes from other devices. If the server's copy differs from
  // what we last synced, another device edited — adopt it locally.
  useEffect(() => {
    if (!stagingHydrated || !user?.email) return;
    const interval = setInterval(async () => {
      try {
        const res = await authFetch('/api/staging');
        if (!res.ok) return;
        const data = await res.json();
        const serverPayload = JSON.stringify(data.items || []);
        if (serverPayload !== lastSyncedRef.current) {
          setBatch(data.items || []);
          lastSyncedRef.current = serverPayload;
        }
      } catch (e) { /* transient — try again next tick */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [stagingHydrated, user?.email, authFetch]);

  // Keep the foil toggle in sync with what the selected printing actually supports.
  // Must run above the auth-gate early return so hook order stays stable across renders.
  const _activePrinting = selectedPrinting || card;
  const _forcedFoil = finishState(_activePrinting).forced;
  useEffect(() => {
    if (_forcedFoil !== null && foil !== _forcedFoil) {
      setFoil(_forcedFoil);
    }
  }, [_forcedFoil, foil]);

  // Auth gate — show login screen until the server confirms the user is authorized
  if (loading || !user) {
    return <Login />;
  }

  const showToast = (msg) => {
    setToast({ msg, show: true });
    setTimeout(() => setToast({ msg: '', show: false }), 2500);
  };

  const handleCardSelect = (cardData, printingsData, hasMore) => {
    setCard(cardData);
    setPrintings(printingsData);
    setPrintingsPage(1);
    setPrintingsHasMore(!!hasMore);
    setSelectedPrinting(cardData);
    setFoil(false);
    setCustomPrice('');
    setQuantity(1);
    setCondition('Near Mint');
  };

  const handlePrintingSelect = (p) => {
    setSelectedPrinting(p);
  };

  const loadMorePrintings = async () => {
    if (!card?.name || !printingsHasMore || loadingMorePrintings) return;
    setLoadingMorePrintings(true);
    try {
      const next = printingsPage + 1;
      const res = await authFetch(`/api/printings?name=${encodeURIComponent(card.name)}&page=${next}`);
      const data = await res.json();
      setPrintings(prev => [...prev, ...(data.printings || [])]);
      setPrintingsPage(next);
      setPrintingsHasMore(!!data.has_more);
    } catch (e) { /* noop */ }
    setLoadingMorePrintings(false);
  };

  const p = selectedPrinting || card;
  const finishInfo = finishState(p);
  const isEtchedPrinting = finishInfo.isEtched;

  const getPrice = () => {
    if (customPrice) return customPrice;
    if (isEtchedPrinting && p?.prices_usd_etched) return p.prices_usd_etched;
    if (foil && p?.prices_usd_foil) return p.prices_usd_foil;
    if (p?.prices_usd) return p.prices_usd;
    if (p?.prices_usd_foil) return p.prices_usd_foil;
    return '';
  };

  const addToBatch = async () => {
    if (!p) return;
    const price = getPrice();

    // Etched Scryfall printings carry their TCGPlayer product ID on
    // tcgplayer_etched_id, not tcgplayer_id.
    const effectivePid = isEtchedPrinting
      ? (p.tcgplayer_etched_id || p.tcgplayer_id)
      : p.tcgplayer_id;

    // Fetch the TCGPlayer SKU ID on demand (condition + finish specific).
    // If the standard foil lookup misses, the backend may return a special-foil
    // sibling product (rainbow foil, etc.) keyed off the card name — in that
    // case we also swap the TCGPlayer product ID to the sibling.
    let tcgplayerSkuId = '';
    let effectivePidFinal = effectivePid || '';
    let specialFoilMatch = null;
    if (effectivePid) {
      try {
        const finishParam = isEtchedPrinting ? '&finish=etched' : '';
        const nameParam = `&name=${encodeURIComponent(p.name || '')}`;
        const res = await authFetch(
          `/api/sku?pid=${effectivePid}&condition=${encodeURIComponent(condition)}&foil=${foil}${finishParam}${nameParam}`
        );
        const data = await res.json();
        tcgplayerSkuId = data.sku_id || '';
        if (data.special_foil_match?.tcg_pid) {
          effectivePidFinal = data.special_foil_match.tcg_pid;
          specialFoilMatch = data.special_foil_match;
        }
      } catch (e) {
        // SKU lookup failed — CSV will have empty ID, not a blocker
      }
    }

    setBatch(prev => [...prev, {
      name: p.name,
      set: p.set_name,
      setCode: p.set_code,
      number: p.collector_number,
      tcgplayerId: effectivePidFinal,
      tcgplayerSkuId,
      condition, foil, owner,
      listedDate: formatDate(),
      url: card.scryfall_url || '',
      image: p.image_normal || '',
      curValue: price,
      quantity,
      tcgCondition: condition,
    }]);
    showToast(specialFoilMatch
      ? `Added ${p.name} — matched ${specialFoilMatch.tcg_name}`
      : `Added ${p.name}`);
    clearForm();
    setSearchResetSignal(s => s + 1);
  };

  const clearForm = () => {
    setCard(null);
    setPrintings([]);
    setSelectedPrinting(null);
  };

  const removeFromBatch = (idx) => setBatch(b => b.filter((_, i) => i !== idx));

  const handleImported = (data) => {
    const listedDate = formatDate();
    const newItems = (data.items || []).map(it => ({ ...it, owner, listedDate }));
    setBatch(prev => [...prev, ...newItems]);
    setShowImport(false);
    const parts = [`Imported ${data.imported}`];
    if (data.skipped_price) parts.push(`skipped ${data.skipped_price} below min`);
    if (data.not_found?.length) parts.push(`${data.not_found.length} unresolved`);
    showToast(parts.join(' · '));
  };

  const copyForSheet = () => {
    const h = 'CARD NAME\tSET\tCARD #\tCONDITION\tFOIL\tOWNER\tListed Date\tURL\tCurValue';
    const rows = batch.map(i =>
      `${i.name}\t${i.set}\t${i.number}\t${i.condition}\t${i.foil ? 'Yes' : 'No'}\t${i.owner}\t${i.listedDate}\t${i.url}\t${i.curValue}`
    ).join('\n');
    navigator.clipboard.writeText(h + '\n' + rows).then(() => showToast('Copied for Google Sheets!'));
  };

  const csvField = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;

  const TCG_HEADER = 'TCGplayer Id,Product Line,Set Name,Product Name,Title,Number,Rarity,Condition,TCG Market Price,TCG Direct Low,TCG Low Price With Shipping,TCG Low Price,Total Quantity,Add to Quantity,TCG Marketplace Price,Photo URL';

  const tcgRow = (i) => {
    const e = csvField('');
    return [
      csvField(i.tcgplayerSkuId),   // col 1:  TCGplayer Id (SKU ID — condition+foil specific)
      e, e, e, e, e, e, e,          // col 2-8: empty (reference only, do not change)
      e, e, e, e, e,                // col 9-13: empty
      csvField(i.quantity),         // col 14: Add to Quantity
      csvField(i.tcgLow ?? i.curValue), // col 15: TCG Marketplace Price (live floor, else Scryfall)
      e,                            // col 16: Photo URL
    ].join(',');
  };

  const copyForTCG = () => {
    const rows = batch.map(tcgRow).join('\n');
    navigator.clipboard.writeText(TCG_HEADER + '\n' + rows).then(() => showToast('Copied TCGPlayer CSV!'));
  };

  const refreshPrices = async () => {
    if (!batch.length) return;
    setRefreshing(true);
    try {
      const ship = parseFloat(shipRate) || 0;
      const res = await authFetch(`/api/prices/refresh?ship=${ship}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batch),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Refresh failed');
      let matched = 0;
      let fellBack = 0;
      const nextBatch = batch.map((item, i) => {
        const r = data.prices?.[i] || {};
        const p = r.price;
        const next = { ...item };
        // curValue stays the Scryfall valuation (flows to the sheet / totals).
        if (p !== null && p !== undefined) next.curValue = String(p);
        // tcgLow is the ephemeral listing price used only for the TCG upload.
        if (r.tcgLow !== null && r.tcgLow !== undefined) {
          next.tcgLow = String(r.tcgLow);
          next.priceSource = r.source || 'scryfall';
          if (r.source === 'matched') matched += 1;
          else fellBack += 1;
        }
        return next;
      });
      setBatch(nextBatch);
      showToast(
        fellBack
          ? `Matched ${matched}/${batch.length} to TCG low (− $${ship.toFixed(2)} ship) · ${fellBack} couldn't match`
          : `Matched ${matched}/${batch.length} to TCG low (− $${ship.toFixed(2)} ship)`
      );
    } catch (err) {
      showToast(`Refresh failed: ${err.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const startFinalize = () => {
    const missing = batch.filter(i => !i.tcgplayerSkuId);
    if (missing.length === 0) {
      finalizeBatch(batch);
      return;
    }
    const pushable = batch.filter(i => i.tcgplayerSkuId);
    setPushWarning({ pushable, missing });
  };

  const finalizeBatch = async (itemsToPush) => {
    if (!itemsToPush.length) {
      showToast('Nothing to push');
      return;
    }
    // Push to the sheet first. If that fails, bail early so the user keeps their batch
    // and can retry without losing work.
    let pushed;
    let demo = false;
    try {
      const res = await authFetch(`/api/sheets/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemsToPush),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unknown error');
      pushed = data.pushed;
      demo = !!data.demo;
    } catch (err) {
      showToast(`Sheet error: ${err.message}`);
      return;
    }

    const rows = itemsToPush.map(tcgRow).join('\n');
    const blob = new Blob([TCG_HEADER + '\n' + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `tcgplayer-upload-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);

    // Only drop the items we actually pushed — anything skipped for missing
    // SKUs stays in the batch so the user can fix or remove it.
    const pushedSet = new Set(itemsToPush);
    setBatch(prev => prev.filter(i => !pushedSet.has(i)));
    setPushWarning(null);
    showToast(demo
      ? `Demo mode · CSV downloaded (sheet not updated)`
      : `Pushed ${pushed} to sheet · CSV downloaded`);
  };

  const totalValue = batch.reduce((s, i) => s + (parseFloat(i.curValue) || 0) * (i.quantity || 1), 0);
  const totalCards = batch.reduce((s, i) => s + (i.quantity || 1), 0);

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-inner">
          <div className="logo-area">
            <div className="logo">⚔</div>
            <div>
              <h1 className="title">Card Lister</h1>
              <p className="subtitle">Search · List · Export</p>
            </div>
          </div>
          <div className="header-right">
            {batch.length > 0 && (
              <div className="header-stats">
                <span className="stat-num">{batch.length}</span> unique ·{' '}
                <span className="stat-num">{totalCards}</span> card{totalCards === 1 ? '' : 's'} ·{' '}
                <span className="stat-num">{formatPrice(totalValue.toString())}</span> total
              </div>
            )}
            <div className="user-menu">
              <button
                className="btn-user-action"
                onClick={() => setShowInventory(true)}
                title="Search your listed inventory"
              >
                🔍 Inventory
              </button>
              <button
                className="btn-user-action"
                onClick={() => setShowImport(true)}
                title="Import ManaBox CSV"
              >
                📥 Import
              </button>
              <button
                className="btn-user-action"
                onClick={() => setShowPriceUpdate(true)}
                title="Update TCG Marketplace prices from a TCG CSV export"
              >
                💰 Update Prices
              </button>
              <button
                className="btn-user-action"
                onClick={() => setShowMatchTx(true)}
                title="Match a TCGPlayer order PDF to sheet rows and mark them sold"
              >
                🔗 Match Transactions
              </button>
              <button
                className="btn-user-action"
                onClick={() => setShowMatchPay(true)}
                title="Reconcile a TCGPlayer payment batch PDF and mark cards paid"
              >
                💵 Match Payments
              </button>
              {user.role === 'admin' && (
                <button
                  className="btn-user-action"
                  onClick={() => setShowAdmin(true)}
                  title="Manage whitelist"
                >
                  👥 Admin
                </button>
              )}
              <span className="user-email" title={user.email}>{user.email}</span>
              <button className="btn-user-action" onClick={logout} title="Sign out">
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
      {showInventory && <Inventory onClose={() => setShowInventory(false)} />}
      {showImport && <ImportManaBox onClose={() => setShowImport(false)} onImported={handleImported} />}
      {showPriceUpdate && <UpdatePrices onClose={() => setShowPriceUpdate(false)} userEmail={user.email} />}
      {showMatchTx && <MatchTransactions onClose={() => setShowMatchTx(false)} userEmail={user.email} />}
      {showMatchPay && <MatchPayments onClose={() => setShowMatchPay(false)} />}
      {editingIdx !== null && batch[editingIdx] && (
        <EditBatchItem
          item={batch[editingIdx]}
          onClose={() => setEditingIdx(null)}
          onSave={(updated) => {
            setBatch(b => b.map((it, i) => (i === editingIdx ? updated : it)));
            setEditingIdx(null);
          }}
        />
      )}
      {pushWarning && (
        <div className="admin-overlay" onClick={() => setPushWarning(null)}>
          <div className="admin-panel" onClick={(e) => e.stopPropagation()}>
            <div className="admin-header">
              <div className="admin-title">Missing TCG SKUs</div>
              <button className="admin-close" onClick={() => setPushWarning(null)}>✕</button>
            </div>
            <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 13, color: '#ddd' }}>
                <strong>{pushWarning.missing.length}</strong> {pushWarning.missing.length === 1 ? 'card has' : 'cards have'} no
                TCGPlayer SKU and can't be uploaded. The remaining <strong>{pushWarning.pushable.length}</strong>{' '}
                will be pushed; the ones below stay in your batch so you can fix or remove them.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '40vh', overflowY: 'auto' }}>
                {pushWarning.missing.map((it, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 10, padding: 8,
                    border: '1px solid #333', borderRadius: 6, alignItems: 'center',
                  }}>
                    {it.image && (
                      <img src={it.image} alt={it.name}
                        style={{ width: 36, height: 50, objectFit: 'cover', borderRadius: 3 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{it.name}</div>
                      <div style={{ fontSize: 12, color: '#999' }}>
                        {it.set} · #{it.number}{it.foil ? ' · Foil' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="btn-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn-cancel" onClick={() => setPushWarning(null)}>Cancel</button>
                <button
                  className="btn-add"
                  onClick={() => finalizeBatch(pushWarning.pushable)}
                  disabled={!pushWarning.pushable.length}
                >
                  Push {pushWarning.pushable.length} · skip {pushWarning.missing.length}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="main">
        {/* Search */}
        <CardSearch onSelect={handleCardSelect} resetSignal={searchResetSignal} />

        {/* Card form */}
        {card && p && (
          <div className="form-area">
            <div className="form-layout">
              <div className="card-preview">
                {p.image_normal && <img src={p.image_normal} alt={p.name} className="card-image" />}
                <div className="card-meta">
                  <div className="card-name">{p.name}</div>
                  <div className="card-set-text">{p.set_name} #{p.collector_number}</div>
                  <div className="card-type">{p.type_line}</div>
                  <div className="price-row">
                    {p.prices_usd && <span className="price-tag">USD ${p.prices_usd}</span>}
                    {p.prices_usd_foil && <span className="price-tag-foil">Foil ${p.prices_usd_foil}</span>}
                    {p.prices_usd_etched && <span className="price-tag-foil">Etched ${p.prices_usd_etched}</span>}
                  </div>
                </div>
              </div>

              <div className="form-fields">
                <div className="field-row">
                  <div className="field">
                    <div className="condition-foil-row">
                      <div className="condition-part">
                        <label className="label">Condition</label>
                        <div className="radio-group">
                          {CONDITIONS.map(c => (
                            <label key={c} className={`radio-option${condition === c ? ' selected' : ''}`}>
                              <input type="radio" name="condition" value={c} checked={condition === c} onChange={() => setCondition(c)} />
                              {c.replace('Near Mint','NM').replace('Lightly Played','LP').replace('Moderately Played','MP').replace('Heavily Played','HP').replace('Damaged','DMG')}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="foil-field">
                        <label className="label">{finishInfo.isEtched ? 'Etched' : 'Foil'}</label>
                        <div
                          className={`toggle ${foil ? 'on' : ''} ${finishInfo.disabled ? 'disabled' : ''}`}
                          onClick={() => { if (!finishInfo.disabled) setFoil(!foil); }}
                          title={
                            finishInfo.isEtched ? 'Etched printing — toggle not applicable' :
                            finishInfo.forced === true  ? 'This printing is foil-only' :
                            finishInfo.forced === false ? 'This printing is non-foil only' : ''
                          }>
                          <div className="toggle-dot" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="field-row">
                  <div className="field">
                    <label className="label">Owner</label>
                    <select value={owner} onChange={e => setOwner(e.target.value)}>
                      {OWNERS.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                </div>

                <div className="field-row">
                  <div className="field">
                    <label className="label">Quantity</label>
                    <input type="number" min={1} value={quantity}
                      onChange={e => setQuantity(parseInt(e.target.value) || 1)} />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field full">
                    <label className="label">Custom Price (leave blank for Scryfall)</label>
                    <input type="text" value={customPrice} onChange={e => setCustomPrice(e.target.value)}
                      placeholder={getPrice() ? `$${getPrice()}` : 'Enter price'} />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field full">
                    <label className="label">Listing Price</label>
                    <div className="final-price">{formatPrice(getPrice())}</div>
                  </div>
                </div>

                <div className="btn-row">
                  <button className="btn-cancel" onClick={clearForm}>Clear</button>
                  <button className="btn-add" onClick={addToBatch}>+ Add to Batch</button>
                </div>
              </div>
            </div>

            <PrintingSelector
              printings={printings}
              selected={selectedPrinting}
              onSelect={handlePrintingSelect}
              onLoadMore={printingsHasMore ? loadMorePrintings : null}
              loadingMore={loadingMorePrintings}
            />
          </div>
        )}

        {/* Batch */}
        {batch.length > 0 && (() => {
          const q = filterQuery.trim().toLowerCase();
          // Keep the original batch index so edit/remove still target the right row.
          const visibleRows = batch
            .map((item, idx) => ({ item, idx }))
            .filter(({ item }) =>
              !q ||
              (item.name || '').toLowerCase().includes(q) ||
              (item.setCode || '').toLowerCase().includes(q) ||
              (item.set || '').toLowerCase().includes(q)
            );
          return (
          <div className="batch-area">
            <div className="batch-header">
              <h3 className="batch-title">
                Batch ({batch.length} unique · {totalCards} card{totalCards === 1 ? '' : 's'}{q && visibleRows.length !== batch.length ? ` · ${visibleRows.length} shown` : ''})
              </h3>
              <input
                type="text"
                value={filterQuery}
                onChange={e => setFilterQuery(e.target.value)}
                placeholder="Filter cards…"
                style={{ flex: '1 1 200px', maxWidth: 280, padding: '6px 10px', borderRadius: 6, border: '1px solid #444', background: '#1a1a1a', color: '#eee' }}
              />
              <div className="batch-actions">
                <div ref={toolsRef} style={{ position: 'relative', display: 'inline-block' }}>
                  <button className="btn-export btn-sheet" onClick={() => setToolsOpen(o => !o)}>🔧 Tools ▾</button>
                  {toolsOpen && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: '#222', border: '1px solid #444', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.5)', zIndex: 50, minWidth: 200, display: 'flex', flexDirection: 'column', padding: 4 }}>
                      <button className="btn-export btn-sheet" style={{ textAlign: 'left', borderRadius: 4 }} onClick={() => { copyForSheet(); setToolsOpen(false); }}>📋 Copy Sheet</button>
                      <button className="btn-export btn-tcg" style={{ textAlign: 'left', borderRadius: 4 }} onClick={() => { copyForTCG(); setToolsOpen(false); }}>📋 Copy TCG</button>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', fontSize: 12, color: '#bbb' }}>
                        Your shipping $
                        <input
                          type="number" step="0.01" min="0"
                          value={shipRate}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setShipRate(e.target.value)}
                          style={{ width: 64, padding: '2px 6px', borderRadius: 4, border: '1px solid #444', background: '#1a1a1a', color: '#eee' }}
                        />
                      </label>
                      <button className="btn-export btn-sheet" style={{ textAlign: 'left', borderRadius: 4 }} disabled={refreshing} onClick={() => { refreshPrices(); setToolsOpen(false); }}>
                        {refreshing ? '⟳ Refreshing…' : '↻ Refresh to TCG Low'}
                      </button>
                    </div>
                  )}
                </div>
                <button className="btn-export btn-push" onClick={startFinalize}>✓ Push & Download CSV</button>
                <button className="btn-export btn-clear" onClick={() => { if (window.confirm('Clear all cards from batch?')) setBatch([]); }}>🗑 Clear</button>
              </div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Card</th><th>Set</th><th>#</th><th>Cond</th>
                    <th>Foil</th><th>Owner</th><th>Price</th><th>Qty</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(({ item, idx }) => {
                    const onEnter = (e) => item.image && setHoverPreview({ image: item.image, x: e.clientX, y: e.clientY, idx });
                    const onMove  = (e) => item.image && setHoverPreview(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
                    const onLeave = () => setHoverPreview(null);
                    return (
                      <tr key={idx} onMouseEnter={onEnter} onMouseMove={onMove} onMouseLeave={onLeave}>
                        <td>{item.name}</td>
                        <td>{item.setCode}</td>
                        <td>{item.number}</td>
                        <td>{item.condition}</td>
                        <td>{item.foil ? '✦' : '—'}</td>
                        <td>{item.owner}</td>
                        <td>
                          {item.tcgLow != null ? (
                            <span title={
                              item.priceSource === 'matched' ? 'Matched: lowest listing total − your shipping'
                              : item.priceSource === 'floor' ? 'TCGPlayer item floor (listings unavailable — not shipping-matched)'
                              : 'Scryfall fallback — no live TCG listing'}>
                              {formatPrice(item.tcgLow)}
                              {item.priceSource !== 'matched' && ' ⚠'}
                            </span>
                          ) : formatPrice(item.curValue)}
                        </td>
                        <td>{item.quantity}</td>
                        <td>
                          <button className="btn-remove" onClick={() => setEditingIdx(idx)} title="Edit">✎</button>
                          <button className="btn-remove" onClick={() => removeFromBatch(idx)} title="Remove">✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          );
        })()}

        {/* Empty state */}
        {!card && batch.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🃏</div>
            <p className="empty-text">Search for a card above to start listing</p>
            <p className="empty-hint">Powered by Scryfall · Export to Google Sheets & TCGPlayer</p>
          </div>
        )}
      </main>

      {/* Toast */}
      <div className={`toast ${toast.show ? 'show' : ''}`}>{toast.msg}</div>

      {/* Row-hover card preview */}
      {hoverPreview && (
        <img
          src={hoverPreview.image}
          alt=""
          style={{
            position: 'fixed',
            // Flip to the left of the cursor if there's no room on the right;
            // same for vertical so we never get cut off by the viewport edge.
            left: hoverPreview.x + 260 > window.innerWidth ? hoverPreview.x - 260 : hoverPreview.x + 20,
            top:  hoverPreview.y + 360 > window.innerHeight ? window.innerHeight - 360 : hoverPreview.y + 10,
            width: 240,
            pointerEvents: 'none',
            zIndex: 1000,
            borderRadius: 10,
            boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
          }}
        />
      )}
    </div>
  );
}

export default App;
